import React from 'react';
import { useLocation } from 'react-router-dom';
import { extractLanguageFromPath } from '@/utils/routing/multilingualRouting';
import { useLanguage } from '@/context/LanguageContext';
import HeroSection from '@/components/home/HeroSection';
import CompactTrustStats from '@/components/home/CompactTrustStats';
import ConversionOptimizedFeatures from '@/components/home/ConversionOptimizedFeatures';
import StrategicInternalLinking from '@/components/home/StrategicInternalLinking';
import HowItWorksSection from '@/components/home/HowItWorksSection';
import TestimonialsSection from '@/components/home/TestimonialsSection';
import CtaSection from '@/components/home/CtaSection';
import AvailableOnlineSection from '@/components/home/AvailableOnlineSection';
import FeaturedTherapistsSection from '@/components/home/FeaturedTherapistsSection';
import EnhancedSEOSystem from '@/components/seo/EnhancedSEOSystem';
import AdvancedStructuredData from '@/components/seo/AdvancedStructuredData';
import CoreWebVitalsOptimizer from '@/components/seo/CoreWebVitalsOptimizer';
import TrustSignals from '@/components/seo/TrustSignals';
import EnhancedFAQSchema from '@/components/seo/EnhancedFAQSchema';
import { useSEOData } from '@/hooks/useSEOData';
import { CityLanguage } from '@/context/languages/languageData';
import { RealTimeSocialProof } from '@/components/conversion/RealTimeSocialProof';
import { ExitIntentPopup } from '@/components/conversion/ExitIntentPopup';
import { MobileStickyCTA } from '@/components/conversion/MobileStickyCTA';
import { LiveChatWidget } from '@/components/conversion/LiveChatWidget';

const getLocalizedSEOData = (language: CityLanguage) => {
  const seoTranslations = {
    en: {
      title: 'Find Therapists, Psychologists & Psychotherapists Worldwide | Global Therapy Connect',
      description: 'Connect with certified mental health professionals worldwide. Find therapists, psychologists & psychotherapists in your language and location. Secure online and in-person therapy sessions.'
    },
    es: {
      title: 'Encuentra Terapeutas, Psicólogos y Psicoterapeutas en Todo el Mundo | Terapia Global',
      description: 'Conéctate con profesionales de salud mental certificados en todo el mundo. Encuentra terapeutas, psicólogos y psicoterapeutas en tu idioma y ubicación. Sesiones seguras de terapia online y presencial.'
    },
    fr: {
      title: 'Trouvez des Thérapeutes, Psychologues et Psychothérapeutes dans le Monde | Thérapie Globale',
      description: 'Connectez-vous avec des professionnels de santé mentale certifiés dans le monde entier. Trouvez des thérapeutes, psychologues et psychothérapeutes dans votre langue et lieu.'
    },
    de: {
      title: 'Finden Sie Therapeuten, Psychologen und Psychotherapeuten Weltweit | Globale Therapie',
      description: 'Verbinden Sie sich mit zertifizierten Fachkräften für psychische Gesundheit weltweit. Finden Sie Therapeuten, Psychologen und Psychotherapeuten in Ihrer Sprache und an Ihrem Standort.'
    },
    it: {
      title: 'Trova Terapeuti, Psicologi e Psicoterapeuti in Tutto il Mondo | Terapia Globale',
      description: 'Connettiti con professionisti della salute mentale certificati in tutto il mondo. Trova terapeuti, psicologi e psicoterapeuti nella tua lingua e posizione.'
    },
    pt: {
      title: 'Encontre Terapeutas, Psicólogos e Psicoterapeutas no Mundo | Terapia Global', 
      description: 'Conecte-se com profissionais de saúde mental certificados em todo o mundo. Encontre terapeutas, psicólogos e psicoterapeutas em seu idioma e localização.'
    }
  };

  return seoTranslations[language] || seoTranslations.en;
};

const MultilingualHomePage = () => {
  const location = useLocation();
  const { t } = useLanguage();
  
  // For home page, always use the detected language from URL path
  // If we're on root (/) use English, otherwise use detected language
  const detectedLanguage = location.pathname === '/' ? 'en' : (extractLanguageFromPath(location.pathname) || 'en');
  const localizedSEO = getLocalizedSEOData(detectedLanguage);
  
  const seoData = useSEOData({
    pageType: 'home',
    customData: {
      title: localizedSEO.title,
      description: localizedSEO.description,
      ogImage: 'https://psychotherapist.help/og-home.png'
    }
  });

  return (
    <>
      {/* Conversion Optimization Components */}
      <RealTimeSocialProof />
      <ExitIntentPopup />
      <MobileStickyCTA variant="find" />
      
      {/* SEO Components */}
      <EnhancedSEOSystem 
        pageData={seoData}
        pageType="home"
      />
      
      <AdvancedStructuredData
        pageType="home"
        ratings={{ averageRating: 4.9, reviewCount: 2847 }}
        contactInfo={{
          phone: "+1-800-THERAPY",
          email: "support@psychotherapist.help"
        }}
        services={[
          { name: "Global Therapist Directory", description: "Access to verified mental health professionals worldwide" },
          { name: "Online Therapy Platform", description: "Secure video therapy sessions" },
          { name: "Professional Training", description: "Master's Degree in Brief Psychotherapy" }
        ]}
      />
      
      <CoreWebVitalsOptimizer 
        pageType="home"
        optimizations={{
          preloadCriticalResources: true,
          optimizeImages: true,
          prefetchRoutes: true,
          enableResourceHints: true
        }}
      />

      {/* Main Content - Optimized Order */}
      <HeroSection />
      <CompactTrustStats />
      <HowItWorksSection />
      <AvailableOnlineSection />
      <TestimonialsSection />
      <ConversionOptimizedFeatures />
      <FeaturedTherapistsSection />
      <StrategicInternalLinking />
      
      <TrustSignals
        pageType="home"
        showRatings={true}
        showSecurity={true}   
        showAccreditations={true}
        showStatistics={true}
      />
      
      <EnhancedFAQSchema
        pageType="home"
        showSearch={true}
        showCategories={true}
        maxVisible={8}
      />
      
      <CtaSection />
      
      {/* Live Chat Widget */}
      <LiveChatWidget />
    </>
  );
};

export default MultilingualHomePage;